import axiosInstance from "../api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";

// ==================== Type Definitions ====================

// TimeRange model for absolute/relative date filtering
export interface TimeRange {
  mode: 'relative' | 'absolute';
  hours: number;
  startTime: string | null;  // ISO 8601
  endTime: string | null;    // ISO 8601
  _id: number;  // for change detection
}

export const buildTimeRangeParams = (timeRange: TimeRange): string => {
  if (timeRange.mode === 'absolute' && timeRange.startTime && timeRange.endTime) {
    return `startTime=${encodeURIComponent(timeRange.startTime)}&endTime=${encodeURIComponent(timeRange.endTime)}`;
  }
  return `hours=${timeRange.hours}`;
};

export const createRelativeTimeRange = (hours: number): TimeRange => ({
  mode: 'relative',
  hours,
  startTime: null,
  endTime: null,
  _id: Date.now(),
});

export const createAbsoluteTimeRange = (startTime: string, endTime: string): TimeRange => ({
  mode: 'absolute',
  hours: 0,
  startTime,
  endTime,
  _id: Date.now(),
});

export interface SystemOverview {
  timestamp: string;
  hours: number;
  health: "HEALTHY" | "DEGRADED" | "CRITICAL";
  metrics: {
    totalRequests: number | null;
    totalErrors: number | null;
    errorRate: number;
    avgLatencyMs: number | null;
    p95LatencyMs: number | null;
    sessionsUploaded: number;
    newUsers: number;
    activeUsers: number;
    note?: string;
  };
  integrations: {
    moosend: string;
    garmin: string;
    strava: string;
    sendgrid: string;
  };
  alerts: SystemAlert[];
}

export interface SystemAlert {
  severity: "INFO" | "WARNING" | "CRITICAL";
  component: string;
  message: string;
}

export interface ServiceHealth {
  name: string;
  displayName: string;
  status: "HEALTHY" | "DEGRADED" | "CRITICAL" | "UNKNOWN";
  metrics: {
    requests: number | null;
    errors: number | null;
    errorRate: number | null;
    avgLatencyMs: number | null;
    p95LatencyMs: number | null;
    p99LatencyMs: number | null;
    maxLatencyMs: number | null;
    note?: string;
  };
  resources?: {
    cpuUsage: number | null;
    memoryUsageMB: number | null;
    instanceCount: number | null;
  };
  alerts: SystemAlert[];
  note?: string;
}

export interface ServicesResponse {
  timestamp: string;
  hours: number;
  services: ServiceHealth[];
  note?: string;
}

export interface MoosendSyncLog {
  startedAt: string;
  completedAt: string | null;
  usersAdded: number;
  usersUpdated: number;
  usersDeleted: number;
  errors: number;
  status: string;
  errorMessage: string | null;
}

export interface VideoExportTimeSeriesPoint {
  timestamp: number;
  total: number;
  created: number;
  requested: number;
  errors: number;
  downloaded: number;
}

export interface SessionUploadTimeSeriesPoint {
  timestamp: number;
  sessions: number;
  users: number;
  jumps: number;
}

export interface JobsResponse {
  timestamp: string;
  hours: number;
  moosendSyncs: MoosendSyncLog[];
  videoExports: {
    requested?: number;
    created?: number;
    error?: number;
    downloaded?: number;
    pendingTotal?: number;
  };
  videoExportTimeSeries?: VideoExportTimeSeriesPoint[];
  sessionUploadTimeSeries?: SessionUploadTimeSeriesPoint[];
}

export interface IntegrationStatus {
  health: "HEALTHY" | "DEGRADED" | "WARNING" | "UNKNOWN";
  [key: string]: unknown;
}

export interface MoosendSyncHistoryEntry {
  syncStarted: string | null;
  syncCompleted: string | null;
  status: string | null;
  usersAdded: number;
  usersUpdated: number;
  errors: number;
  isLatest: boolean;
}

export interface MoosendIntegration extends IntegrationStatus {
  lastSyncStarted: string | null;
  lastSyncCompleted: string | null;
  lastSyncStatus: string | null;
  lastUsersAdded: number;
  lastUsersUpdated: number;
  lastErrors: number;
  stateDistribution: Record<string, number>;
  syncHistory?: MoosendSyncHistoryEntry[];
}

export interface GarminIntegration extends IntegrationStatus {
  connectedUsers: number | string;
  note?: string;
  // BigQuery-based import stats
  successCount?: number;
  rejectedCount?: number;
  errorCount?: number;
  totalExecutions?: number;
  importTimeSeries?: GarminImportTimeSeriesPoint[];
}

export interface GarminImportTimeSeriesPoint {
  timestamp: number;
  success: number;
  rejected: number;
  errors: number;
}

export interface ReceiptIntegration extends IntegrationStatus {
  total: number;
  active: number;
  errors: number;
  ios: number;
  android: number;
}

export interface GarminTimeSeriesPoint {
  timestamp: number;
  imports: number;
  users: number;
}

// Cloud Functions stats
export interface CloudFunctionStats {
  name: string;
  executions: number;
  success: number;
  errors: number;
  avgDurationMs: number | null;
  generation: string;
}

export interface CloudFunctionsStats {
  health: "HEALTHY" | "DEGRADED" | "WARNING" | "UNKNOWN";
  functions: CloudFunctionStats[];
  totalExecutions: number;
  totalErrors: number;
  timeSeries?: CloudFunctionsTimeSeriesPoint[];
  note?: string;
}

export interface CloudFunctionsTimeSeriesPoint {
  timestamp: number;
  executions: number;
  errors: number;
  // Per-function counts (dynamic keys based on function names)
  [key: string]: number;
}

// Cloud Tasks stats
export interface CloudTasksQueue {
  name: string;
  taskCount: number;
}

export interface CloudTasksStats {
  health: "HEALTHY" | "DEGRADED" | "WARNING" | "UNKNOWN";
  queues: CloudTasksQueue[];
  totalTasks: number;
  timeSeries?: CloudTasksTimeSeriesPoint[];
  note?: string;
}

export interface CloudTasksTimeSeriesPoint {
  timestamp: number;
  tasks: number;
  // Per-queue counts (dynamic keys based on queue names)
  [key: string]: number;
}

// Cron Jobs stats
export interface CronJob {
  endpoint: string;
  executions: number;
  success: number;
  errors: number;
  avgDurationMs: number | null;
  lastRun: string;
}

export interface CronJobsStats {
  health: "HEALTHY" | "DEGRADED" | "WARNING" | "UNKNOWN";
  jobs: CronJob[];
  totalExecutions: number;
  totalErrors: number;
  timeSeries?: CronJobsTimeSeriesPoint[];
  note?: string;
}

export interface CronJobsTimeSeriesPoint {
  timestamp: number;
  executions: number;
  errors: number;
}

// Pub/Sub stats
export interface PubSubTopic {
  name: string;
  messageCount: number;
}

export interface PubSubStats {
  health: "HEALTHY" | "DEGRADED" | "WARNING" | "UNKNOWN";
  topics: PubSubTopic[];
  totalMessages: number;
  timeSeries?: PubSubTimeSeriesPoint[];
  note?: string;
}

export interface PubSubTimeSeriesPoint {
  timestamp: number;
  messages: number;
  // Per-topic counts (camelCase versions of topic names)
  garminSessionImports?: number;
  jumpVideoExports?: number;
  surfrSessionSaveEvents?: number;
  surfrSessionLiveEvents?: number;
  surfrSessionEventsPartnerTwg?: number;
  systemAlertNotifications?: number;
}

export interface IntegrationsResponse {
  timestamp: string;
  hours: number;
  moosend: MoosendIntegration;
  garmin: GarminIntegration;
  videoExport: {
    requested?: number;
    created?: number;
    error?: number;
    downloaded?: number;
    pendingTotal?: number;
  };
  receipts: ReceiptIntegration;
  garminTimeSeries?: GarminTimeSeriesPoint[];
  // New fields
  cloudFunctions?: CloudFunctionsStats;
  cloudTasks?: CloudTasksStats;
  cronJobs?: CronJobsStats;
  pubsub?: PubSubStats;
}

export interface ConnectionPoolMetrics {
  health: string;
  poolName?: string;
  maximumPoolSize?: number;
  activeConnections?: number;
  idleConnections?: number;
  totalConnections?: number;
  threadsAwaitingConnection?: number;
  error?: string;
}

export interface TableStats {
  name: string;
  rowCount: number;
  size: string;
  sizeBytes?: number;
}

export interface MaterializedViewStats {
  name: string;
  size: string;
  sizeBytes?: number;
  rowCount?: number;
  lastRefresh?: string | null;
  note?: string;
}

export interface DatabaseResponse {
  timestamp: string;
  connectionPool: ConnectionPoolMetrics;
  tables: TableStats[];
  materializedViews: MaterializedViewStats[];
}

export interface UserCounts {
  total: number;
  newInPeriod: number;
  pro: number;
  freeSubscription: number;
}

export interface SubscriptionStatus {
  iosActive: number;
  androidActive: number;
  errors: number;
}

export interface ActivityMetrics {
  sessionsUploaded: number;
  jumpsRecorded: number;
  activeLivestreams: number;
}

export interface UserActivityTimeSeriesPoint {
  timestamp: number;
  newUsers: number;
  activeUsers?: number;
  sessions?: number;
}

export interface UsersResponse {
  timestamp: string;
  hours: number;
  counts: UserCounts;
  subscriptions: SubscriptionStatus;
  activity: ActivityMetrics;
  timeSeries?: UserActivityTimeSeriesPoint[];
}

export interface ReceiptError {
  userId: number;
  platform: string;
  error: string;
  lastCheck: string;
  errorCount: number;
}

export interface MoosendError {
  startedAt: string;
  errorMessage: string;
  errorCount: number;
}

export interface VideoExportError {
  id: number;
  userId: number;
  created: string;
}

export interface ErrorTimeSeriesPoint {
  timestamp: number;
  serverErrors: number;
  clientErrors: number;
  http500: number;
  http502: number;
  http503: number;
  http504: number;
  totalRequests: number;
}

export interface ErrorsResponse {
  timestamp: string;
  hours: number;
  receiptErrors: ReceiptError[];
  moosendErrors: MoosendError[];
  videoExportErrors: VideoExportError[];
  apiErrorsNote?: string;
  timeSeries?: ErrorTimeSeriesPoint[];
}

export interface TimeSeriesDataPoint {
  timestamp: number; // epoch seconds
  requests: number;
  errors: number;
  clientErrors: number;
  errorRate: number;
  avgLatencyMs: number | null;
  p50LatencyMs: number | null;
  p95LatencyMs: number | null;
  p99LatencyMs: number | null;
  uniqueIps: number;
}

export interface TimeSeriesResponse {
  timestamp: string;
  hours: number;
  interval: string;
  data: TimeSeriesDataPoint[];
}

export interface EndpointMetrics {
  endpoint: string;
  method: string;
  requests: number;
  serverErrors: number;
  clientErrors: number;
  errorRate: number;
  avgLatencyMs: number | null;
  p50LatencyMs: number | null;
  p95LatencyMs: number | null;
  p99LatencyMs: number | null;
  maxLatencyMs: number | null;
}

export interface EndpointsResponse {
  timestamp: string;
  service: string;
  hours: number;
  endpoints: EndpointMetrics[];
}

export interface ApiError {
  timestamp: string;
  traceId: string | null;
  requestId: string | null;
  service: string;
  endpoint: string;
  method: string;
  status: number;
  latencyMs: number | null;
  ip: string | null;
  userAgent: string | null;
}

export interface ApiErrorsResponse {
  timestamp: string;
  hours: number;
  errors: ApiError[];
  count: number;
}

export interface TraceLogEntry {
  timestamp: string;
  severity: string;
  logName: string;
  message?: string;
  protoPayload?: {
    resource: string;
    method: string;
    status: number | null;
    latency: string | null;
  };
  sourceLocation?: {
    file: string;
    line: string;
    function: string;
  };
}

export interface TraceLogsResponse {
  timestamp: string;
  traceId: string;
  logs: TraceLogEntry[];
  count: number;
}

// ==================== APM Trace Types ====================

export interface TraceEntry {
  timestamp: number;        // epoch seconds
  traceId: string;
  requestId: string | null;
  service: string;
  endpoint: string;
  method: string;
  status: number;
  latencyMs: number | null;
  ip: string | null;
  userAgent: string | null;
  responseSize: number | null;
  host: string | null;
  // Full-text search highlight
  matchedLog?: {
    timestamp: string;
    severity: string;
    message: string;
  };
}

export interface TracesResponse {
  timestamp: string;
  traces: TraceEntry[];
  count: number;
  offset: number;
  hasMore: boolean;
}

export interface TraceFilters {
  service: string | null;
  endpoint: string | null;
  method: string | null;
  minStatus: number | null;
  maxStatus: number | null;
  minLatencyMs: number | null;
  maxLatencyMs: number | null;
  traceId: string | null;
  textSearch: string | null;
  userId: string | null;
}

export interface SpanEntry {
  requestId: string;
  service: string;
  endpoint: string;
  method: string;
  status: number;
  latencyMs: number;
  startTimestamp: number;   // epoch ms
  endTimestamp: number;     // epoch ms
  responseSize: number | null;
  ip: string | null;
  userAgent: string | null;
  host: string | null;
  referrer: string | null;
  cost: number | null;
  taskName: string | null;
  taskQueueName: string | null;
}

export interface TraceSpansResponse {
  traceId: string;
  spans: SpanEntry[];
}

export interface PaginatedTraceLogEntry {
  timestamp: string;
  severity: string;
  message: string | null;
  spanId: string | null;
  service: string;
  sourceLocation?: {
    file: string | null;
    line: string | null;
    function: string | null;
  };
}

export interface PaginatedTraceLogsResponse {
  traceId: string;
  logs: PaginatedTraceLogEntry[];
  count: number;
  offset: number;
  hasMore: boolean;
}

// ==================== Trace Analyze Types ====================

export interface AnalyzeGroup {
  key: string;
  requests: number;
  errors: number;
  errorRate: number;
  avgLatencyMs: number | null;
  p50LatencyMs: number | null;
  p95LatencyMs: number | null;
  p99LatencyMs: number | null;
  maxLatencyMs: number | null;
}

export interface AnalyzeTimeSeriesPoint {
  timestamp: number;
  groups: Record<string, {
    requests: number;
    errors: number;
    avgLatencyMs: number | null;
    p95LatencyMs: number | null;
  }>;
}

export interface AnalyzeResponse {
  timestamp: string;
  groupBy: string;
  groups: AnalyzeGroup[];
  timeSeries?: AnalyzeTimeSeriesPoint[];
}

export interface AnalyzeFilters {
  service: string | null;
  endpoint: string | null;
  method: string | null;
  minStatus: number | null;
  maxStatus: number | null;
  minLatencyMs: number | null;
  maxLatencyMs: number | null;
}

// ==================== Search & User Trace Types ====================

export interface SearchTracesResponse {
  timestamp: string;
  searchText: string;
  traces: TraceEntry[];
  count: number;
  offset: number;
  hasMore: boolean;
}

export interface UserDeviceInfo {
  id: number;
  model: string | null;
  brand: string | null;
  os: string | null;
  accessToken: string; // truncated
}

export interface UserInfo {
  id: number;
  email: string;
  name: string | null;
}

export interface UserTraceEntry extends TraceEntry {
  device?: {
    id: number;
    model: string | null;
    os: string | null;
  };
}

export interface UserTracesResponse {
  user: UserInfo;
  devices: UserDeviceInfo[];
  traces: UserTraceEntry[];
  count: number;
  offset: number;
  hasMore: boolean;
}

export interface EndpointTimeSeriesPoint {
  timestamp: number;
  requests: number;
  errors: number;
  clientErrors: number;
  errorRate: number;
  avgLatencyMs: number | null;
  p50LatencyMs: number | null;
  p95LatencyMs: number | null;
  p99LatencyMs: number | null;
  maxLatencyMs: number | null;
}

export interface EndpointTimeSeriesResponse {
  timestamp: string;
  service: string;
  endpoint: string;
  method: string;
  hours: number;
  interval: string;
  data: EndpointTimeSeriesPoint[];
}

export interface EndpointError {
  timestamp: string;
  traceId: string | null;
  requestId: string | null;
  endpoint: string;
  method: string;
  status: number;
  latencyMs: number | null;
}

export interface EndpointErrorsResponse {
  timestamp: string;
  service: string;
  endpoint: string;
  hours: number;
  errors: EndpointError[];
  count: number;
}

// ==================== Service Implementation ====================

export const SystemStatusService = {
  /**
   * Get system health overview
   */
  async getOverview(accesstoken: string, hours: number = 24, timeRange?: TimeRange): Promise<SystemOverview | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.OVERVIEW}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching system overview:", error);
      return null;
    }
  },

  /**
   * Get per-service health metrics
   */
  async getServices(accesstoken: string, hours: number = 1, timeRange?: TimeRange): Promise<ServicesResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.SERVICES}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching service health:", error);
      return null;
    }
  },

  /**
   * Get job execution status
   */
  async getJobs(accesstoken: string, hours: number = 24, timeRange?: TimeRange): Promise<JobsResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.JOBS}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching job status:", error);
      return null;
    }
  },

  /**
   * Get external integration health
   */
  async getIntegrations(accesstoken: string, hours: number = 24, timeRange?: TimeRange): Promise<IntegrationsResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.INTEGRATIONS}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching integration status:", error);
      return null;
    }
  },

  /**
   * Get database health metrics
   */
  async getDatabase(accesstoken: string): Promise<DatabaseResponse | null> {
    try {
      const response = await axiosInstance.get(
        ENDPOINTS.ADMIN.SYSTEM.DATABASE,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching database health:", error);
      return null;
    }
  },

  /**
   * Get user activity metrics
   */
  async getUsers(accesstoken: string, hours: number = 24, timeRange?: TimeRange): Promise<UsersResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.USERS}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching user metrics:", error);
      return null;
    }
  },

  /**
   * Get error summary
   */
  async getErrors(accesstoken: string, hours: number = 24, timeRange?: TimeRange): Promise<ErrorsResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.ERRORS}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching error summary:", error);
      return null;
    }
  },

  /**
   * Get time series metrics for charts
   */
  async getTimeSeries(
    accesstoken: string,
    hours: number = 24,
    interval: string = "hour",
    timeRange?: TimeRange
  ): Promise<TimeSeriesResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.METRICS_TIMESERIES}?${params}&interval=${interval}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching time series metrics:", error);
      return null;
    }
  },

  /**
   * Get time series metrics for a specific service
   */
  async getServiceTimeSeries(
    accesstoken: string,
    service: string,
    hours: number = 24,
    interval: string = "hour",
    timeRange?: TimeRange
  ): Promise<TimeSeriesResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.SERVICE_TIMESERIES(service)}?${params}&interval=${interval}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching service time series metrics:", error);
      return null;
    }
  },

  /**
   * Get endpoint metrics for a specific service
   */
  async getServiceEndpoints(
    accesstoken: string,
    service: string,
    hours: number = 1,
    timeRange?: TimeRange
  ): Promise<EndpointsResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.SERVICE_ENDPOINTS(service)}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching service endpoints:", error);
      return null;
    }
  },

  /**
   * Get recent API errors
   */
  async getApiErrors(
    accesstoken: string,
    hours: number = 24,
    limit: number = 50,
    service?: string,
    timeRange?: TimeRange
  ): Promise<ApiErrorsResponse | null> {
    try {
      const params = timeRange ? buildTimeRangeParams(timeRange) : `hours=${hours}`;
      let url = `${ENDPOINTS.ADMIN.SYSTEM.API_ERRORS}?${params}&limit=${limit}`;
      if (service) {
        url += `&service=${encodeURIComponent(service)}`;
      }
      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${accesstoken}`,
        },
      });

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching API errors:", error);
      return null;
    }
  },

  /**
   * Get trace logs for a specific error (legacy Cloud Logging endpoint)
   */
  async getTraceLogs(
    accesstoken: string,
    traceId: string
  ): Promise<TraceLogsResponse | null> {
    try {
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.TRACE_LOGS(traceId)}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching trace logs:", error);
      return null;
    }
  },

  /**
   * Get paginated traces with filtering (APM Trace Explorer)
   */
  async getTraces(
    accesstoken: string,
    timeRange: TimeRange,
    filters: TraceFilters,
    limit: number = 50,
    offset: number = 0,
    sortBy: string = 'timestamp',
    sortOrder: string = 'desc'
  ): Promise<TracesResponse | null> {
    try {
      const params = new URLSearchParams();
      // Time range
      if (timeRange.mode === 'absolute' && timeRange.startTime && timeRange.endTime) {
        params.set('startTime', timeRange.startTime);
        params.set('endTime', timeRange.endTime);
      } else {
        params.set('hours', timeRange.hours.toString());
      }
      params.set('limit', limit.toString());
      params.set('offset', offset.toString());
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      // Filters
      if (filters.service) params.set('service', filters.service);
      if (filters.endpoint) params.set('endpoint', filters.endpoint);
      if (filters.method) params.set('method', filters.method);
      if (filters.minStatus !== null) params.set('minStatus', filters.minStatus.toString());
      if (filters.maxStatus !== null) params.set('maxStatus', filters.maxStatus.toString());
      if (filters.minLatencyMs !== null) params.set('minLatencyMs', filters.minLatencyMs.toString());
      if (filters.maxLatencyMs !== null) params.set('maxLatencyMs', filters.maxLatencyMs.toString());
      if (filters.traceId) params.set('traceId', filters.traceId);
      if (filters.textSearch) params.set('textSearch', filters.textSearch);
      if (filters.userId) params.set('userId', filters.userId);

      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.TRACES}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching traces:", error);
      return null;
    }
  },

  /**
   * Get paginated application logs for a trace (BigQuery-based)
   */
  async getPaginatedTraceLogs(
    accesstoken: string,
    traceId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<PaginatedTraceLogsResponse | null> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.TRACES_LOGS(traceId)}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching paginated trace logs:", error);
      return null;
    }
  },

  /**
   * Get all request spans for a trace (cross-service waterfall)
   */
  async getTraceSpans(
    accesstoken: string,
    traceId: string
  ): Promise<TraceSpansResponse | null> {
    try {
      const response = await axiosInstance.get(
        ENDPOINTS.ADMIN.SYSTEM.TRACES_SPANS(traceId),
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching trace spans:", error);
      return null;
    }
  },

  /**
   * Analyze traces with grouping and aggregation
   */
  async analyzeTraces(
    accesstoken: string,
    timeRange: TimeRange,
    groupBy: string = 'service',
    filters: AnalyzeFilters = { service: null, endpoint: null, method: null, minStatus: null, maxStatus: null, minLatencyMs: null, maxLatencyMs: null },
    options: { timeSeries?: boolean; interval?: string; limit?: number } = {}
  ): Promise<AnalyzeResponse | null> {
    try {
      const params = new URLSearchParams();
      if (timeRange.mode === 'absolute' && timeRange.startTime && timeRange.endTime) {
        params.set('startTime', timeRange.startTime);
        params.set('endTime', timeRange.endTime);
      } else {
        params.set('hours', timeRange.hours.toString());
      }
      params.set('groupBy', groupBy);
      if (filters.service) params.set('service', filters.service);
      if (filters.endpoint) params.set('endpoint', filters.endpoint);
      if (filters.method) params.set('method', filters.method);
      if (filters.minStatus !== null) params.set('minStatus', filters.minStatus.toString());
      if (filters.maxStatus !== null) params.set('maxStatus', filters.maxStatus.toString());
      if (filters.minLatencyMs !== null) params.set('minLatencyMs', filters.minLatencyMs.toString());
      if (filters.maxLatencyMs !== null) params.set('maxLatencyMs', filters.maxLatencyMs.toString());
      if (options.timeSeries) params.set('timeSeries', 'true');
      if (options.interval) params.set('interval', options.interval);
      if (options.limit) params.set('limit', options.limit.toString());

      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.TRACES_ANALYZE}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error analyzing traces:", error);
      return null;
    }
  },

  /**
   * Full-text search across log messages, returns traces with matching log entries
   */
  async searchTraces(
    accesstoken: string,
    timeRange: TimeRange,
    text: string,
    service: string | null = null,
    limit: number = 50,
    offset: number = 0
  ): Promise<SearchTracesResponse | null> {
    try {
      const params = new URLSearchParams();
      if (timeRange.mode === 'absolute' && timeRange.startTime && timeRange.endTime) {
        params.set('startTime', timeRange.startTime);
        params.set('endTime', timeRange.endTime);
      } else {
        params.set('hours', timeRange.hours.toString());
      }
      params.set('text', text);
      params.set('limit', limit.toString());
      params.set('offset', offset.toString());
      if (service) params.set('service', service);

      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.TRACES_SEARCH}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error searching traces:", error);
      return null;
    }
  },

  /**
   * Get traces for a specific user by email or userId
   */
  async getUserTraces(
    accesstoken: string,
    timeRange: TimeRange,
    email: string | null = null,
    userId: string | null = null,
    deviceId: number | null = null,
    limit: number = 50,
    offset: number = 0
  ): Promise<UserTracesResponse | null> {
    try {
      const params = new URLSearchParams();
      if (timeRange.mode === 'absolute' && timeRange.startTime && timeRange.endTime) {
        params.set('startTime', timeRange.startTime);
        params.set('endTime', timeRange.endTime);
      } else {
        params.set('hours', timeRange.hours.toString());
      }
      if (email) params.set('email', email);
      if (userId) params.set('userId', userId);
      if (deviceId !== null) params.set('deviceId', deviceId.toString());
      params.set('limit', limit.toString());
      params.set('offset', offset.toString());

      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.TRACES_USER}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching user traces:", error);
      return null;
    }
  },

  /**
   * Get time series metrics for a specific endpoint
   */
  async getEndpointTimeSeries(
    accesstoken: string,
    service: string,
    endpoint: string,
    method: string,
    hours: number = 24,
    interval: string = "hour",
    timeRange?: TimeRange
  ): Promise<EndpointTimeSeriesResponse | null> {
    try {
      const params = new URLSearchParams({
        endpoint: endpoint,
        method: method,
        interval: interval,
      });
      if (timeRange && timeRange.mode === 'absolute' && timeRange.startTime && timeRange.endTime) {
        params.set('startTime', timeRange.startTime);
        params.set('endTime', timeRange.endTime);
      } else {
        params.set('hours', hours.toString());
      }
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.ENDPOINT_TIMESERIES(service)}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching endpoint time series:", error);
      return null;
    }
  },

  /**
   * Get errors for a specific endpoint
   * @param minStatus - minimum status code (default 400 for all errors)
   * @param maxStatus - maximum status code exclusive (e.g., 500 for warnings only)
   */
  async getEndpointErrors(
    accesstoken: string,
    service: string,
    endpoint: string,
    method: string,
    hours: number = 24,
    limit: number = 20,
    minStatus: number = 400,
    maxStatus?: number,
    timeRange?: TimeRange
  ): Promise<EndpointErrorsResponse | null> {
    try {
      const params = new URLSearchParams({
        endpoint: endpoint,
        method: method,
        limit: limit.toString(),
        minStatus: minStatus.toString(),
      });
      if (timeRange && timeRange.mode === 'absolute' && timeRange.startTime && timeRange.endTime) {
        params.set('startTime', timeRange.startTime);
        params.set('endTime', timeRange.endTime);
      } else {
        params.set('hours', hours.toString());
      }
      if (maxStatus !== undefined) {
        params.append("maxStatus", maxStatus.toString());
      }
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.SYSTEM.ENDPOINT_ERRORS(service)}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching endpoint errors:", error);
      return null;
    }
  },
};
